import { useState, useCallback, useRef, KeyboardEvent, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Image, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/atoms/button';
import { Textarea } from '@/components/atoms/textarea';
import { validateImageFile, compressImage, MAX_IMAGE_SIZE } from '@/lib/utils/image';

interface MessageInputProps {
  onSendText: (text: string) => void;
  onSendImage?: (file: File) => void;
  isLoading?: boolean;
  disabled?: boolean;
  maxLength?: number;
}

export function MessageInput({
  onSendText,
  onSendImage,
  isLoading = false,
  disabled = false,
  maxLength = 250,
}: MessageInputProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = useCallback(() => {
    const trimmedText = text.trim();
    if (trimmedText && !isLoading && !disabled) {
      onSendText(trimmedText);
      setText('');
    }
  }, [text, isLoading, disabled, onSendText]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleImageClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !onSendImage) return;

      // Reset input immediately
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Validate file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast.error(t('messaging.invalidFileType', 'Format de fichier non supporté. Veuillez sélectionner une image.'));
        return;
      }

      try {
        let processedFile = file;

        // Compress if file is larger than MAX_IMAGE_SIZE (3MB)
        if (file.size > MAX_IMAGE_SIZE) {
          setIsCompressing(true);
          toast.info(t('messaging.compressingImage', 'Compression de l\'image en cours...'));
          processedFile = await compressImage(file);
        }

        onSendImage(processedFile);
      } catch (error) {
        console.error('Error processing image:', error);
        toast.error(t('messaging.imageProcessingError', 'Erreur lors du traitement de l\'image'));
      } finally {
        setIsCompressing(false);
      }
    },
    [onSendImage, t]
  );

  const canSend = text.trim().length > 0 && !isLoading && !disabled && !isCompressing;
  const remainingChars = maxLength - text.length;
  const showCharCount = text.length > maxLength * 0.8;
  const isProcessing = isLoading || isCompressing;

  return (
    <div className="flex items-end gap-3 p-4 border-t bg-gradient-to-t from-muted/30 to-background">
      {onSendImage && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            className="hidden"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleImageClick}
            disabled={isProcessing || disabled}
            className="shrink-0 size-10 rounded-full hover:bg-muted transition-colors"
          >
            {isCompressing ? (
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            ) : (
              <Image className="size-5 text-muted-foreground" />
            )}
          </Button>
        </>
      )}

      <div className="flex-1 relative">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, maxLength))}
          onKeyDown={handleKeyDown}
          placeholder={t('messaging.typeMessage', 'Tapez votre message...')}
          disabled={disabled}
          rows={1}
          className="min-h-[44px] max-h-[120px] resize-none pr-10 rounded-2xl bg-muted/50 border-muted-foreground/20 focus:border-primary/50 focus:ring-primary/20 transition-all"
        />
        {showCharCount && (
          <span
            className={cn(
              'absolute right-3 bottom-3 text-[10px] font-medium tabular-nums transition-colors',
              remainingChars < 20 ? 'text-destructive' : 'text-muted-foreground/60'
            )}
          >
            {remainingChars}
          </span>
        )}
      </div>

      <Button
        type="button"
        size="icon"
        onClick={handleSend}
        disabled={!canSend}
        className={cn(
          'shrink-0 size-10 rounded-full transition-all duration-200',
          canSend
            ? 'bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:scale-105'
            : 'bg-muted hover:bg-muted'
        )}
      >
        {isLoading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Send className="size-4" />
        )}
      </Button>
    </div>
  );
}
